import { IPermissionProps } from '@/types/shared'
import SubHeaders from '@/components/Headers/SubHeaders'
import { useTranslation } from 'react-i18next'
import { paths } from '@/utils/path'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    getTicketByID,
    convertIncidentToProblem,
    escalateTicketByID,
} from '../../services'
import { Alert, Tabs, Typogaphy } from 'vnetwork-ui'
import { useState } from 'react'
import useTabs from '@/hooks/useTabs'
import { tabs, tabsWithoutLogs } from './tabs'
import LinkTicket from './LinkTicket'
import InternalRatingDialog from '@/modules/InternalRating/components/InternalRatingDialog'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import PreviewServiceInformation from '../Forms/PreviewServiceInformation'
import TicketLog from './TicketLogs'
import LoadIcons from '@/icons/LoadIcons'
import ServiceNotificationMailerTracking from '@/modules/Service/components/Detail/Templates/shared/ServiceNotifications/Tracking'
import TicketSLADowntimePreview from './TicketSLADowntimePreview'
import { ERROR_SERVICE_INCIDENT_TICKET_KEY } from '../../constants'
import { TicketHeader, TicketInfoSidebar, TicketContent } from './sections'
import { useConfirmation } from '@/contexts/confirmation'
import { useQueryClient } from '@tanstack/react-query'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import StickyActionBar from './TicketReports/StickyActionBar'

// Statuses that allow editing
const EDITABLE_STATUSES = ['opening', 'work-in-progress', 'escalated']

const Detail: React.FC<IPermissionProps> = () => {
    const { ticketID } = useParams<{ ticketID: string }>()
    const { t } = useTranslation()
    const { t: tTicket } = useTranslation('ticket')
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const [openLinkDialog, setOpenLinkDialog] = useState(false)

    // Deep-link nhắc đánh giá: /tickets/:id?rate=1 auto-mở dialog (H-14: dialog, không popup chặn).
    const rateDialogOpen = searchParams.get('rate') === '1'
    const closeRateDialog = () =>
        setSearchParams(
            (prev) => {
                prev.delete('rate')
                return prev
            },
            { replace: true }
        )
    const { setConfirmation } = useConfirmation()
    const queryClient = useQueryClient()

    const { data: ticket, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKET_DETAIL, ticketID],
        queryFn: async () => {
            const result = await getTicketByID({
                ticket_id: Number(ticketID) || 0,
            })
            return result
        },
    })

    const ticketData = ticket?.result

    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticketData?.ticket_id}`,
    })

    // Convert incident to problem mutation
    const { mutateAsync: convertMutation, isPending: isConverting } =
        useMutationWithHandler(
            { mutationFn: convertIncidentToProblem },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('updated-success'),
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.TICKET_DETAIL, ticketID],
                    })
                    refetchLogs()
                },
            }
        )

    // Mobile: all tabs including logs
    const { TabComponent: MobileTabComponent, TabProps: MobileTabProps } =
        useTabs({ tabs: tabs(t, ticketData), className: 'text-xs' })
    // Desktop: tabs without logs (logs shown in sidebar)
    const { TabComponent: DesktopTabComponent, TabProps: DesktopTabProps } =
        useTabs({ tabs: tabsWithoutLogs(t, ticketData), className: 'text-xs' })

    const canEdit = EDITABLE_STATUSES.includes(ticketData?.ticket_status || '')
    const hasNoAssignees =
        Array.isArray(ticketData?.ticket_technical_assignees) &&
        ticketData.ticket_technical_assignees.length <= 0

    // Can convert: Incident type that is NOT major incident
    const canConvert =
        ticketData?.ticket_type_key === 'error_service_incident' &&
        ticketData?.ticket_marjorable?.[0] !== true

    // Escalate ticket mutation (IT-ISMS QT-01 Bước 5)
    const { mutateAsync: escalateMutation } = useMutationWithHandler(
        { mutationFn: escalateTicketByID },
        {
            reactQueryKeys: QUERY_KEYS.TICKETS,
            successMessage: 'Ticket đã được escalate',
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.TICKET_DETAIL, ticketID],
                })
                refetchLogs()
            },
        }
    )

    const handleEscalate = () => {
        if (!ticketData?.ticket_id) return

        setConfirmation({
            open: true,
            title: 'Escalate Ticket',
            description:
                'Bạn có chắc muốn escalate ticket này? Hành động này sẽ thông báo cho Team Leader / Trưởng ban IT-ISMS.',
            useReason: true,
            onConfirm: async (reason?: string | null) => {
                await escalateMutation({
                    ticket_id: ticketData.ticket_id,
                    reason: reason || 'Cần escalate',
                })
            },
        })
    }

    const handleConvertToProblem = () => {
        if (!ticketData?.ticket_id) return

        setConfirmation({
            open: true,
            title: t('ticket-convert-title'),
            description: t('ticket-convert-description'),
            onConfirm: async () => {
                await convertMutation({
                    ticket_id: ticketData.ticket_id,
                    content: ticketData.ticket_content || '',
                })
            },
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    { path: paths.ticket, title: t('ticket-list') },
                    { path: '', title: `#${ticketData?.ticket_id}` },
                ]}
            />

            {/* Alert for no assignees */}
            {hasNoAssignees && (
                <Alert size="small" variant="info">
                    {t('ticket-technical-assignees-alert')}
                </Alert>
            )}

            {/* Header with badges and actions */}
            {ticketData && (
                <TicketHeader
                    ticket={ticketData}
                    canEdit={canEdit}
                    onLinkTicket={() => setOpenLinkDialog(true)}
                    canConvert={canConvert}
                    onConvertToProblem={handleConvertToProblem}
                    isConverting={isConverting}
                />
            )}

            {/* Main Layout */}
            <div className="flex flex-col lg:flex-row gap-2">
                {/* Main Content Area */}
                <div className="flex-1 space-y-2 min-w-0">
                    {/* Combined Card: Description + Service Config + Process */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        {/* Content Description */}
                        {ticketData && (
                            <TicketContent ticket={ticketData} asSection />
                        )}

                        {/* Preview Service Information */}
                        {ticketData?.service_id &&
                            ticketData?.service_id > 0 && (
                                <PreviewServiceInformation
                                    serviceId={ticketData.service_id}
                                    ticketId={ticketData.ticket_id}
                                    ticketTypeKey={ticketData.ticket_type_key}
                                    defaultExpanded={
                                        ticketData?.ticket_type_group ===
                                        'service'
                                    }
                                    asSection
                                />
                            )}

                        {/* Tabs Section - Mobile (includes logs) */}
                        <div className="lg:hidden" data-tabs-section>
                            <Tabs {...MobileTabProps} />
                            {MobileTabComponent}
                        </div>

                        {/* Tabs Section - Desktop (without logs) */}
                        <div className="hidden lg:block" data-tabs-section>
                            <Tabs {...DesktopTabProps} />
                            {DesktopTabComponent}
                        </div>
                    </div>

                    {/* Notification Tracking */}
                    {ticketData?.notification_status === 'sent' &&
                        ticketData?.service_id &&
                        ticketData?.service_id > 0 && (
                            <ServiceNotificationMailerTracking
                                serviceID={ticketData.service_id}
                            />
                        )}

                    {/* SLA & Downtime Preview */}
                    {ticketData?.ticket_type_key ===
                        ERROR_SERVICE_INCIDENT_TICKET_KEY &&
                        ticketData?.service_id &&
                        ticketData?.service_id > 0 && (
                            <TicketSLADowntimePreview
                                serviceId={ticketData.service_id}
                                ticketId={ticketData.ticket_id}
                            />
                        )}

                    {/* Spacer for sticky action bar */}
                    <div className="h-16" />
                </div>

                {/* Right Sidebar - Info + Logs */}
                <div className="lg:w-80 xl:w-96 shrink-0 space-y-2 order-first lg:order-last">
                    <div className="lg:sticky lg:top-2 space-y-2">
                        {/* Ticket Info */}
                        {ticketData && (
                            <TicketInfoSidebar
                                ticket={ticketData}
                                onNavigateToLinkedTicket={(id) =>
                                    navigate(`/tickets/${id}`)
                                }
                            />
                        )}

                        {/* Logs - Desktop only */}
                        <div className="hidden lg:block bg-white rounded-lg border border-gray-200">
                            <div className="p-3 border-b border-gray-200 flex items-center gap-2">
                                <LoadIcons className="text-primary w-4 h-4" />
                                <Typogaphy className="font-medium text-sm">
                                    {tTicket('logs')}
                                </Typogaphy>
                            </div>
                            <div className="overflow-y-auto max-h-[400px]">
                                <TicketLog />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Link Ticket Dialog */}
            <LinkTicket
                open={openLinkDialog}
                onClose={() => setOpenLinkDialog(false)}
                ticket={ticketData}
            />

            {/* Internal rating dialog (?rate=1) */}
            {ticketData?.ticket_id && (
                <InternalRatingDialog
                    ticketId={ticketData.ticket_id}
                    open={rateDialogOpen}
                    onClose={closeRateDialog}
                />
            )}

            {/* Sticky Action Bar */}
            {ticketData && (
                <StickyActionBar
                    ticket={ticketData}
                    onEscalate={handleEscalate}
                    onScrollToForm={() => {
                        // Scroll to tabs section
                        const tabsSection = document.querySelector(
                            '[data-tabs-section]'
                        )
                        tabsSection?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }}
                />
            )}
        </div>
    )
}

export default Detail
